import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useLayoutEffect } from "react";

/**
 * Culinex Professional · Theme + Density System
 * ─────────────────────────────────────────────
 * Theme modes: "light" | "dark" | "system"
 *   Applied on <html> via `data-theme` attribute + `.light` / `.dark` class.
 *   Persists in localStorage.culinex_theme_mode.
 *
 * Density modes: "comfortable" | "compact"
 *   Applied on <html> via `data-density` attribute + `.density-compact` class.
 *   Shaves 10–15% off paddings, row heights, and toolbar sizes for chefs
 *   running CulinexOS on smaller pass-through screens.
 *   Persists in localStorage.culinex_density_mode.
 */

import { useBrandIdentity } from "../components/BrandRuntimeGate";
import { applyBrandTheme } from "../lib/counterCultureTheme";

const ThemeContext = createContext(null);
const STORAGE_THEME  = "culinex_theme_mode";
const STORAGE_DENSITY = "culinex_density_mode";

const resolveSystem = () => "light";

const applyThemeToDom = (resolved) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
};

const applyDensityToDom = (density) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-density", density);
  root.classList.remove("density-comfortable", "density-compact");
  root.classList.add(`density-${density}`);
};

export function ThemeProvider({ children }) {
  const { brand_key: brandKey } = useBrandIdentity();
  // Verified workspace controls the theme for every route and portal.
  useLayoutEffect(() => {
    applyBrandTheme(brandKey);
    return () => applyBrandTheme(null);
  }, [brandKey]);
  // Light surfaces remain canonical; verified Counter Culture receives its scoped palette.
  const [mode] = useState("light");
  const resolved = "light";

  const [density, setDensity] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_DENSITY);
      if (stored === "comfortable" || stored === "compact") return stored;
    } catch { /* ignore */ }
    return "comfortable";
  });

  useEffect(() => { applyThemeToDom(resolved); }, [resolved]);
  useEffect(() => { applyDensityToDom(density); }, [density]);

  // The standard remains light; brand colours are controlled centrally.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_THEME, "light"); } catch { /* ignore */ }
    applyThemeToDom("light");
  }, []);

  // Persist density.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_DENSITY, density); } catch { /* ignore */ }
  }, [density]);

  // Theme switching stays disabled; density controls remain available.
  const setTheme = useCallback(() => {}, []);
  const toggleTheme = useCallback(() => {}, []);

  const setDensityMode = useCallback((next) => {
    if (next !== "comfortable" && next !== "compact") return;
    setDensity(next);
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, setTheme, toggleTheme, density, setDensity: setDensityMode, brandTheme: brandKey === "rolls_and_brew" ? "counter-culture" : null }),
    [mode, resolved, setTheme, toggleTheme, density, setDensityMode, brandKey],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
};

// Bootstrap BEFORE React mounts so first paint uses the correct tokens.
export function bootstrapTheme() {
  if (typeof document === "undefined") return;
  let mode = "light";
  let density = "comfortable";
  try {
    // Ignore any legacy saved theme. Butter Light is canonical.
    mode = "light";
    const d = localStorage.getItem(STORAGE_DENSITY);
    if (d === "comfortable" || d === "compact") density = d;
  } catch { /* ignore */ }
  applyThemeToDom("light");
  applyDensityToDom(density);
}
